import * as React from 'react';

import { ApiError, ProblemType, errorMessage, type IWriteRejection } from '@/domains/api';
import { surveyApi } from '../api';
import type {
  AnswerPayload,
  IAnswer,
  IAnswerWrite,
  ICursor,
  IDefinitionSnapshot,
  IFinding,
  IMemberLookupAnswer,
  IRenderState,
  IResponseState,
  IRespondentLookupAnswer,
  ISnapshotUsage,
  LookupStatus,
  SubmitReport
} from '../api/contracts';
import {
  encodeAnswer,
  isChoiceField,
  isMalformed,
  sameAnswer,
  writableCells,
  type UiValue
} from './answer-values';
import { memberShapesOf, respondentShapeOf } from './lookup-shapes';
import { isAddressDrawn, pendingConfirmations, prefillLock } from './prefill-view';
import { predictRender, ruleDisagreements } from './rule-engine';
import { rowWriteColumns } from './table-layout';
import {
  addressKey,
  addressOf,
  buildDefinitionView,
  indexAnswers,
  selectedCodes,
  type IDefinitionView
} from './snapshot-view';

/**
 * A choice is a finished answer the moment it is made, and the engine's rules
 * hang off it — visibility, requiredness, the count that drives a repeat group —
 * so it goes out promptly. The delay only exists so ticking through a checkbox
 * list is one batch instead of one request per box.
 */
const CHOICE_SAVE_MS = 400;

/**
 * Backstop for typed text, which is normally saved on blur. This catches the
 * respondent who types and then walks away without leaving the field; it is
 * deliberately long, because every earlier firing would be a save landing
 * mid-word.
 */
const IDLE_SAVE_MS = 20 * 1000;

/**
 * The two `invalid-action` reasons a confirmation batch comes back with when the decision
 * it carries has already been made — by the respondent answering the field themselves
 * (`overridden`, so no longer `proposed`), by a double click, or by another tab.
 *
 * Read as "nothing left to do here" rather than as a refusal, which is why they are named:
 * `duplicate_item` and `unknown_usage` are bugs in the batch this app built and must stay
 * loud.
 */
const SETTLED_PREFILL_REASONS = new Set(['not_proposed', 'no_provenance']);

/** An unlink refused because the page is stale, not because of anything pressed: the row is gone
 *  (`unknown_row`) or holds the lead now (`lead_row`). A quiet re-read says it. */
const STALE_UNLINK_REASONS = new Set(['unknown_row', 'lead_row']);

/**
 * The words for a refusal of a write or a decision this page should never have offered
 * (SR-416, B5-4), by survey-api's literal reason (`AnswerWriteService`, `ResponseActionService`).
 * They stay loud — a banner, never a quiet retry — because each means the page drew something as
 * enterable that survey-api holds: a lock (`prefillLock`) or a computed, N/A or heading cell
 * (`tableLayout`). A reason not named here is said as spelled (`refusalWords`).
 */
export const REFUSAL_WORDS: Record<string, string> = {
  prefill_locked: 'kept as proposed — it cannot be changed here',
  locked_usage: 'kept as proposed — it cannot be rejected here',
  lead_locked: 'kept as your lead advisor’s — it cannot be changed here',
  cell_read_only: 'worked out from other answers, or has no answer to give'
};

/** A refusal's reason in the respondent's words, else as survey-api spelled it. */
export function refusalWords(reason: string): string {
  return REFUSAL_WORDS[reason] ?? reason;
}

/**
 * What a respondent is told when the cycle closed under them — the one refusal on this
 * surface that no later attempt can get past, so the sentence says what happened rather
 * than asking for another try.
 *
 * survey-api's own title is "The survey cycle is not open.", which reads as a fact about a
 * cycle rather than as an answer to "where did my last answer go".
 */
const CYCLE_CLOSED_MESSAGE =
  'This cycle has closed, so nothing further can be saved. Answers saved before it closed ' +
  'are kept.';

export type ResponseStatus = 'loading' | 'ready' | 'missing' | 'error';

/** The key a CRD lookup is held under: a member row's `instanceId`, or this for the respondent's
 *  own CRD (FSP). */
export const RESPONDENT_LOOKUP = 'respondent';

/**
 * What this session's last CRD lookup for one row said (SR-416, ML §6) — the registries' answer,
 * or `pending` while it is out, `refused` with survey-api's 422 reason, or `failed` when the lookup
 * got no answer (a 5xx, the network) — said beside the CRD like a registry outage, never in the
 * page's banner, since the respondent pressed nothing (the owner's ruling, 2026-10-18). The session's alone:
 * the state document keeps the proposed names, never the status, the firm or the pins (F12), so a
 * reload has no note and the names' own strip says where they came from.
 */
export interface ILookupNote {
  status: LookupStatus | 'pending' | 'refused' | 'failed';
  /**
   * The CRD the answer is about, as the state held it once the lookup landed (`''` while out) —
   * so a note is drawn only beside the CRD it speaks of, and not under one typed or cleared since.
   */
  crd: string;
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  /** A member's firm, or the respondent's broker-dealer (else their RIA firm). Said, never written. */
  firmName: Nullable<string>;
  /** The 422's reason (`crd_missing`, `crd_invalid`, `lookup_not_offered`) when `refused`. */
  reason?: string;
}

export interface IAnswerAddress {
  usage: string;
  instanceId: Nullable<string>;
  instanceKey: string;
}

export interface IUseResponse {
  status: ResponseStatus;
  /** A load failure or a refused action; cleared by the next successful one. */
  error: Nullable<string>;
  definition: Nullable<IDefinitionSnapshot>;
  view: Nullable<IDefinitionView>;
  state: Nullable<IResponseState>;
  /**
   * What the form draws: survey-api's render, with the entries the unsaved answers move as the
   * rules decide them (`runtime/rule-engine.ts`), so a follow-up opens on the click that opens it.
   * survey-api's own value stands wherever this engine cannot tell or tells it otherwise.
   */
  render: Nullable<IRenderState>;

  /** Local edits not yet acknowledged by the server, keyed by address. */
  drafts: Map<string, UiValue>;
  isSaving: boolean;
  isSubmitting: boolean;
  hasPendingWrites: boolean;
  lastSavedAt: Nullable<string>;
  /** Set when the server refused a batch — a client bug by construction. */
  rejections: IWriteRejection[];
  /** Set when submit was gated; the review panel renders from it. */
  submitReport: Nullable<SubmitReport>;
  /**
   * Addresses the respondent has **finished with** — left, or answered by
   * picking. Not addresses typed into: a field mid-typing is not yet wrong, and
   * marking it would put "an answer is required" under the first character of an
   * answer being given. See `visibleFindings`.
   */
  touched: Set<string>;
  /**
   * Addresses holding text no payload of their field type can carry — `abc` in a count.
   *
   * Nothing is queued for them and nothing is cleared: the overlay entry is the only copy
   * of what was typed, so it stays on screen and the field says why it cannot be saved.
   * Rendered only where the address is also in `touched`, on the same "finished with it"
   * rule every other message follows — an error under the first character of a number
   * being typed is the thing that rule exists to prevent.
   */
  malformed: Set<string>;
  /** True once a submit has been attempted: from then on, show everything. */
  revealFindings: boolean;
  /**
   * The sections a forward press was made on ("Save and continue"): their findings show from
   * then on, touched or not (`visibleFindings`). A stepper button reveals nothing.
   */
  revealedSteps: ReadonlySet<string>;
  revealStep: (sectionCode: string) => void;
  /**
   * The cycle was closed under this response, so nothing further can be written to it.
   *
   * Its own flag rather than a status, because the response's own status is unchanged: a
   * draft caught by SR-213's close gate stays `draft` and would keep rendering as editable
   * over a server that refuses every write. The three other refusals are recoverable —
   * re-read, fix, retry — and this one is not, so nothing clears it but a fresh load.
   */
  cycleClosed: boolean;
  /**
   * The response was taken out of play under this form: staff moved it to the recycle bin
   * (SR-470), and every route that names it answers 404, as a stranger's response does. Set by the
   * first write or action that meets the 404, and as final as `cycleClosed`: the batch is not put
   * back, nothing is read again — the re-read is a 404 too, and would replace what is on screen
   * with "Survey not found" — and nothing more is sent. `after` is when the last batch that landed
   * was taken from the queue — what was typed later went in a batch that did not land, or in none
   * — else the read the form opened on.
   */
  withdrawn: Nullable<{ after: string }>;

  setValue: (address: IAnswerAddress, value: UiValue, usage: ISnapshotUsage) => void;
  /**
   * The respondent has left a typed field, so its value is finished — this is
   * what normally saves it. Wired to `blur`, not to a typing timer, because a
   * save that lands while a field is still being typed in rewrites it: the wire
   * wants a number, so `10.` goes out as `10` and comes back as `"10"` under the
   * caret, turning the next keystroke into `105`.
   */
  commit: () => Promise<void>;
  /**
   * Flushes the queued batch now — page changes, "finish later", submit — and resolves to the state
   * the response stands at once it has landed, or null when it did not land (a refusal, no answer,
   * a closed cycle — said in the banner) or before the first load.
   */
  flush: () => Promise<Nullable<IResponseState>>;
  /**
   * This session's CRD lookups, by row (`instanceId`, or `RESPONDENT_LOOKUP`) — see `ILookupNote`.
   * A lookup is the runtime's, never a press: it is called after the flush that wrote a row's CRD,
   * for Teams' row 1 while its CRD is blank, and once for a row whose CRD is a proposal (ML §6).
   */
  lookups: Map<string, ILookupNote>;
  addRow: (usageCode: string) => Promise<void>;
  removeRow: (instanceId: string) => Promise<void>;
  acknowledge: (finding: IFinding) => Promise<void>;
  /**
   * Accepts or refuses prefill proposals — one address, or a whole step's worth.
   *
   * A batch because a returning FA participant meets 77 proposals and the endpoint has
   * always taken `items[]`: one request per decision would be 77 evaluation passes and 77
   * whole state documents. survey-api validates the batch all-or-nothing, so the caller
   * hands over addresses it has just read as `proposed` — `pendingPrefill` is that list.
   */
  confirmPrefill: (
    addresses: IAnswerAddress[],
    action: 'confirm' | 'reject'
  ) => Promise<void>;
  /**
   * "No, not this person" (the survey review's G10): undoes a member row's lookup — survey-api
   * drops the names it proposed, confirmed or locked, and keeps the CRD — and marks the names
   * finished with, so they ask for an answer.
   */
  unlinkMember: (instanceId: string, names: IAnswerAddress[]) => Promise<void>;
  /** Proposals still waiting on a decision, filtered to what the respondent can see. */
  pendingPrefill: IAnswerAddress[];
  /**
   * Addresses emptied by a rejection **in this session**.
   *
   * survey-api deletes a rejected proposal's answer and cascades its provenance row, so
   * nothing on the wire says the field was ever prefilled: after a reload it is simply an
   * empty question. The set exists so the field can say "cleared — please enter your own
   * answer" for as long as the respondent is looking at it, rather than silently going
   * blank under their cursor. It is dropped the moment they answer.
   */
  clearedPrefill: Set<string>;
  saveCursor: (cursor: ICursor) => void;
  submit: () => Promise<boolean>;
  reload: () => Promise<void>;
}

/**
 * One respondent's pass through one response, against survey-api.
 *
 * The division of labour is the API's, not a choice made here: **the server owns
 * the truth**. It evaluates the rules, derives visibility and requiredness,
 * synthesizes findings, materializes repeat rows and computes answers — and
 * returns the whole state document on every mutation. What this hook owns:
 *
 * - a **draft overlay** of edits typed since the last successful write, so the
 *   control stays responsive while a batch is in flight — and the **render
 *   predicted** from it (`render`, `runtime/rule-engine.ts`), so what the rules
 *   open and shut moves with the click, survey-api's render winning on landing;
 * - the **write queue**, coalesced per address, so autosave is one batched
 *   request rather than one per keystroke;
 * - **when that batch goes out**: a choice on a short timer, typed text on blur.
 *   Not on a typing timer — a save that lands mid-word replaces the field's
 *   contents with the server's normalized form of a half-finished value, which
 *   moves the caret and, for anything numeric, silently changes what was typed;
 * - the **revision protocol**: every mutation echoes `revision`, and a 409
 *   re-reads state and replays the queue once rather than losing the edit.
 */
export function useResponse(responseId: Nullable<string>): IUseResponse {
  const [status, setStatus] = React.useState<ResponseStatus>('loading');
  const [error, setError] = React.useState<Nullable<string>>(null);
  const [definition, setDefinition] = React.useState<Nullable<IDefinitionSnapshot>>(null);
  const [state, setState] = React.useState<Nullable<IResponseState>>(null);
  const [drafts, setDrafts] = React.useState<Map<string, UiValue>>(new Map());
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState<Nullable<string>>(null);
  const [rejections, setRejections] = React.useState<IWriteRejection[]>([]);
  const [submitReport, setSubmitReport] = React.useState<Nullable<SubmitReport>>(null);
  const [touched, setTouched] = React.useState<Set<string>>(new Set());
  const [malformed, setMalformed] = React.useState<Set<string>>(new Set());
  const [revealFindings, setRevealFindings] = React.useState(false);
  const [revealedSteps, setRevealedSteps] = React.useState<Set<string>>(new Set());
  const [cycleClosed, setCycleClosed] = React.useState(false);
  const [withdrawn, setWithdrawn] = React.useState<Nullable<{ after: string }>>(null);
  const [clearedPrefill, setClearedPrefill] = React.useState<Set<string>>(new Set());
  const [lookups, setLookups] = React.useState<Map<string, ILookupNote>>(new Map());
  /** `focused`, as state: the field being typed moves the predicted render once it is left. */
  const [typing, setTyping] = React.useState<Nullable<string>>(null);

  // Refs, not state: the flush closure must see the latest queue and revision
  // without re-arming the timers on every keystroke.
  const pending = React.useRef(new Map<string, IAnswerWrite>());
  const revision = React.useRef(0);
  const inflight = React.useRef<Nullable<Promise<void>>>(null);

  // Two timers rather than one shared deadline: a choice must not sit behind the
  // long text backstop just because a text field was touched after it. Whichever
  // fires first drains the whole queue.
  const choiceTimer = React.useRef<Nullable<number>>(null);
  const idleTimer = React.useRef<Nullable<number>>(null);

  /** The address being typed into, so a landing save can leave it alone. */
  const focused = React.useRef<Nullable<string>>(null);
  /** The same address as its triple — `commit` looks a CRD up by it (gate 2's finding E). */
  const focusedAddress = React.useRef<Nullable<IAnswerAddress>>(null);
  /**
   * The addresses of the batch on the wire. Out of `pending` once taken, so `pruneDrafts` would
   * drop their drafts on any other state landing first — a lookup's, typically — and the value just
   * typed would flash back to the stored one until its own write landed.
   */
  const inflightKeys = React.useRef<ReadonlySet<string>>(new Set());

  /** The server's answers, for deciding whether a write would change anything. */
  const answersRef = React.useRef<Map<string, IAnswer>>(new Map());
  /** The state last adopted, for what runs after a write lands (`flush`'s answer, the lookups). */
  const stateRef = React.useRef<Nullable<IResponseState>>(null);
  /** The definition, set as it is loaded — `adopt` runs before the render that would assign it. */
  const definitionRef = React.useRef<Nullable<IDefinitionSnapshot>>(null);
  /** `definitionRef`'s view, built once per definition for `dropHidden`. */
  const viewRef = React.useRef<Nullable<{ of: IDefinitionSnapshot; view: IDefinitionView }>>(null);
  const cycleClosedRef = React.useRef(false);
  cycleClosedRef.current = cycleClosed;
  /**
   * Set at once by `withdraw`, not after the render: a timer or a queued lookup can fire between
   * the 404 and that render, and must find the form already out of writing.
   */
  const withdrawnRef = React.useRef(false);
  /** Up to when this form's typing reached survey-api — what `withdrawn.after` says. */
  const reachedAt = React.useRef<Nullable<string>>(null);

  /**
   * The CRD lookups' bookkeeping (B6-2). `lookedUp` is the CRD a row was last looked up with —
   * a lookup is asked once per value; `asked` the rows a lookup was queued for by the state alone
   * (Teams' blank row 1, a proposed CRD), once per session; `queued` the rows waiting, so a row is
   * never in the queue twice. The queue runs one lookup at a time, in order.
   */
  const lookedUp = React.useRef(new Map<string, string>());
  const askedRows = React.useRef(new Set<string>());
  const queuedRows = React.useRef(new Set<string>());
  const lookupQueue = React.useRef<Promise<void>>(Promise.resolve());
  /** Assigned every render (`reloadRef`'s shape): what `adopt` calls once a state lands, and
   *  what `flush` and `commit` call once a CRD is stored. */
  const afterAdoptRef = React.useRef<Nullable<(next: IResponseState) => void>>(null);
  const lookUpCrdsRef = React.useRef<Nullable<(written: IAnswerAddress[]) => void>>(null);
  // A page kept mounted over another response starts that response's lookups afresh: the
  // bookkeeping is keyed by row, and `RESPONDENT_LOOKUP` is every response's key.
  React.useEffect(() => {
    lookedUp.current.clear();
    askedRows.current.clear();
    queuedRows.current.clear();
    setLookups(new Map());
  }, [responseId]);

  /**
   * The malformed set as a ref, assigned every render — the same shape `reloadRef` uses
   * below and for the same reason. `pruneDrafts` and `commit` both have to know whether an
   * address is holding unparseable text, and it lives in state because the form renders
   * from it; a ref lets them read the current set without depending on it and re-arming on
   * every keystroke.
   */
  const malformedRef = React.useRef<ReadonlySet<string>>(malformed);
  malformedRef.current = malformed;

  /**
   * Addresses waiting on the write that carries their answer.
   *
   * They cannot join `touched` before that write lands. Until it does, the state
   * in hand is the one from *before* the answer was given — it still holds the
   * `engine.required` for the field the respondent has just filled, and revealing
   * it would flash that error for the length of the round trip. `adopt` promotes
   * them, so the answer and the disappearance of its finding arrive together.
   */
  const pendingTouch = React.useRef(new Set<string>());

  const markTouched = React.useCallback((key: string) => {
    setTouched((previous) => (previous.has(key) ? previous : new Set(previous).add(key)));
  }, []);

  const revealStep = React.useCallback((sectionCode: string) => {
    setRevealedSteps((previous) => withKeys(previous, [sectionCode]));
  }, []);

  const markMalformed = React.useCallback((key: string, is: boolean) => {
    setMalformed((previous) => {
      if (previous.has(key) === is) return previous;
      const next = new Set(previous);
      if (is) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const clearTimers = React.useCallback(() => {
    if (choiceTimer.current) window.clearTimeout(choiceTimer.current);
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    choiceTimer.current = null;
    idleTimer.current = null;
  }, []);

  /**
   * The response is gone for this respondent (`withdrawn`): out of writing at once, the queue and
   * its timers dropped. The overlay stays — it is what was typed and not saved, on screen under
   * the form's notice — and the banner comes down, since the notice says what happened.
   */
  const withdraw = React.useCallback(() => {
    withdrawnRef.current = true;
    clearTimers();
    pending.current.clear();
    setWithdrawn((now) => now ?? { after: reachedAt.current ?? new Date().toISOString() });
    setError(null);
  }, [clearTimers]);

  /**
   * Drops overlay entries the server has now answered for — except the one the
   * caret is in. That field's typed text is what the respondent is still working
   * on, and replacing it with the server's normalized form (`10.` → `10`,
   * `'John '` → `'John'`) would edit the field under them. `commit` releases it
   * on blur, where normalizing is exactly what should happen.
   *
   * A **malformed** entry is spared too, and for a stronger reason than the caret. The
   * server has not been told about it — nothing was sent — so "the server has now answered
   * for it" is simply false: pruning would replace what was typed with the previous
   * answer, or with nothing. And it would not even happen at blur, where it might be
   * noticed: `adopt` runs on *any* field's write landing, so the erasure would arrive
   * while the respondent was somewhere else on the page entirely.
   */
  const pruneDrafts = React.useCallback(() => {
    setDrafts((previous) => {
      if (previous.size === 0) return previous;
      const remaining = new Map(previous);
      for (const key of remaining.keys()) {
        if (
          pending.current.has(key) ||
          inflightKeys.current.has(key) ||
          key === focused.current ||
          malformedRef.current.has(key)
        ) {
          continue;
        }
        remaining.delete(key);
      }
      return remaining.size === previous.size ? previous : remaining;
    });
  }, []);

  /**
   * A follow-up its parent's answer has hidden lost its answer on the server, deleted in the
   * write that hid it (survey-api's B1-15; the survey review's R1, the plan's Q9 c), so what was
   * typed there goes too — its draft and a held entry — or the field would come back holding
   * them. Only once nothing is queued or on the wire: the form draws a follow-up from its parent's
   * draft, so a state adopted while a parent's choice is still out would call a follow-up hidden
   * that the respondent is typing into. Never the field the caret is in.
   */
  const dropHidden = React.useCallback((next: IResponseState) => {
    const definition = definitionRef.current;
    if (!definition || pending.current.size > 0 || inflightKeys.current.size > 0) return;
    if (viewRef.current?.of !== definition) {
      viewRef.current = { of: definition, view: buildDefinitionView(definition) };
    }
    const { view } = viewRef.current;
    const hidden = (key: string) => {
      const address = addressOf(key);
      return (
        !!view.usagesByCode.get(address.usage)?.childRole &&
        key !== focused.current &&
        !isAddressDrawn(address, next, view)
      );
    };
    setDrafts((previous) => withoutKeys(previous, [...previous.keys()].filter(hidden)));
    setMalformed((previous) => withoutKeys(previous, [...previous].filter(hidden)));
  }, []);

  const adopt = React.useCallback(
    (next: IResponseState) => {
      setState(next);
      stateRef.current = next;
      revision.current = next.revision;
      answersRef.current = indexAnswers(next.answers);
      // Everything queued has now come back as server state; anything typed
      // while the request was in flight is still queued and stays in the overlay.
      pruneDrafts();
      dropHidden(next);

      // Batched into the same render as `setState`, which is the point: the
      // fields being revealed and the findings that judge them have to change
      // together, or the form shows one against the other's state.
      if (pendingTouch.current.size > 0) {
        const promoted = [...pendingTouch.current];
        pendingTouch.current.clear();
        setTouched((previous) => withKeys(previous, promoted));
      }
      afterAdoptRef.current?.(next);
    },
    [pruneDrafts, dropHidden]
  );

  const load = React.useCallback(
    async (signal?: AbortSignal) => {
      if (!responseId) return;
      setStatus('loading');
      setError(null);

      try {
        // The definition is per-response (non-answer folding depends on the
        // response's pinned facts), so the two are fetched together.
        const [loadedState, loadedDefinition] = await Promise.all([
          surveyApi.getResponse(responseId, signal),
          surveyApi.getDefinition(responseId, signal)
        ]);

        if (signal?.aborted) return;
        if (!loadedState || !loadedDefinition) {
          setStatus('missing');
          return;
        }

        setDefinition(loadedDefinition);
        definitionRef.current = loadedDefinition;
        adopt(loadedState);
        reachedAt.current = new Date().toISOString();
        setStatus('ready');
      } catch (loadError) {
        if (signal?.aborted) return;
        setError(errorMessage(loadError, 'Could not load the survey'));
        setStatus('error');
      }
    },
    [adopt, responseId]
  );

  React.useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  // Assigned every render so the flush closure can re-read after a refusal it
  // cannot fix locally, without `load` and `flush` depending on each other.
  const reloadRef = React.useRef<Nullable<() => Promise<void>>>(null);
  reloadRef.current = () => load();

  /** Sends the queued batch. One flight at a time; callers await the same promise. Resolves to
   *  the state the response stands at once it lands. */
  const flush = React.useCallback(async (): Promise<Nullable<IResponseState>> => {
    if (!responseId) return stateRef.current;
    clearTimers();
    // Taken out of play: nothing lands, and nothing more is sent (`withdrawn`).
    if (withdrawnRef.current) return null;
    if (inflight.current) {
      // Joined, then asked again: what was queued while that flight was out goes in the next one,
      // which the flight's own caller chains — a joiner returning now would answer with a state
      // that has not seen it (gate 2's finding B). With nothing queued this returns at once.
      await inflight.current;
      return flush();
    }
    if (pending.current.size === 0) return stateRef.current;

    let batch = [...pending.current.values()];
    let keys = [...pending.current.keys()];
    pending.current.clear();
    // What was typed after this moment is in a later batch, or none.
    const takenAt = new Date().toISOString();

    /**
     * Drops the writes survey-api's prefill lock or the Teams lead's lock would refuse
     * (`prefill_locked`, `lead_locked`), judged against the answers given — the batch is
     * all-or-nothing, so one of them takes every answer beside it down. Only a race sends one: a
     * looked-up name proposed and locked while the respondent typed it at the old revision (gate
     * 2's finding G, the owner's ruling), or row 1 taking the lead while its CRD was typed. The
     * field then shows the proposal, read-only — "Confirm" where the respondent has more than that
     * to do, its source alone where not (`isConfirmOnly`); its draft goes.
     */
    const dropLocked = (answers: Map<string, IAnswer>) => {
      const locked = new Set<string>();
      keys.forEach((key, index) => {
        const usage = definitionRef.current?.usages.find((each) => each.code === batch[index].usage);
        const answer = answers.get(key);
        if (answer?.locked || (usage && prefillLock(usage, answer) === 'all')) locked.add(key);
      });
      if (locked.size === 0) return;
      batch = batch.filter((_, index) => !locked.has(keys[index]));
      keys = keys.filter((key) => !locked.has(key));
      inflightKeys.current = new Set(keys);
      for (const key of locked) pendingTouch.current.delete(key);
      setDrafts((previous) => {
        const remaining = new Map(previous);
        for (const key of locked) remaining.delete(key);
        return remaining;
      });
    };

    let landed = false;
    inflightKeys.current = new Set(keys);
    const send = (async () => {
      setIsSaving(true);
      try {
        let next: IResponseState;
        dropLocked(answersRef.current);
        try {
          next =
            batch.length === 0
              ? stateRef.current!
              : await surveyApi.writeAnswers(responseId, revision.current, batch);
        } catch (writeError) {
          // A stale revision means someone else moved the response — the other
          // tab, or a server-side write. Re-read and replay the batch once
          // against the revision we now hold; a second conflict is surfaced.
          if (
            writeError instanceof ApiError &&
            writeError.isProblem(ProblemType.revisionConflict)
          ) {
            const fresh = await surveyApi.getResponse(responseId);
            if (!fresh) throw new ResponseGone();
            revision.current = fresh.revision;
            dropLocked(indexAnswers(fresh.answers));
            next =
              batch.length === 0
                ? fresh
                : await surveyApi.writeAnswers(responseId, revision.current, batch);
          } else {
            throw writeError;
          }
        }

        inflightKeys.current = new Set();
        adopt(next);
        landed = true;
        setRejections([]);
        setError(null);
        reachedAt.current = takenAt;
        setLastSavedAt(new Date().toISOString());
      } catch (writeError) {
        if (
          writeError instanceof ApiError &&
          writeError.isProblem(ProblemType.writesRejected)
        ) {
          // All-or-nothing: nothing was applied. The overlay keeps the typed
          // values so the respondent can see and fix what was refused.
          setRejections(writeError.problem?.rejections ?? []);
          setError('Some answers were refused by the survey definition.');
        } else if (
          writeError instanceof ApiError &&
          writeError.isProblem(ProblemType.responseNotWritable)
        ) {
          setError('This response is closed to changes.');
          void reloadRef.current?.();
        } else if (writeError instanceof ApiError && writeError.isProblem(ProblemType.cycleClosed)) {
          // SR-213's close gate, and the one refusal a retry cannot outlive: the cycle is
          // shut, so the next flush is refused exactly as this one was. The batch is
          // deliberately **not** put back — re-queueing it turns every later trigger, the
          // 20-second backstop included, into another permanent 409 — and no re-read is
          // asked for either, because a draft caught by a closing cycle keeps its `draft`
          // status and would come back looking editable. `cycleClosed` is what takes the
          // form out of writing instead.
          setCycleClosed(true);
          setError(CYCLE_CLOSED_MESSAGE);
        } else if (isGone(writeError)) {
          // Taken out of play (`withdrawn`): final as a closed cycle is, and for the same reason
          // not put back — re-queued, every later trigger would meet the same 404.
          withdraw();
        } else {
          // The batch was dropped from the queue when it was taken; putting it
          // back means the next flush (or a manual save) retries it.
          for (let index = 0; index < keys.length; index += 1) {
            if (!pending.current.has(keys[index])) pending.current.set(keys[index], batch[index]);
          }
          setError(errorMessage(writeError, 'Could not save your answers'));
        }
      } finally {
        setIsSaving(false);
        inflight.current = null;
        inflightKeys.current = new Set();
      }
    })();

    inflight.current = send;
    await send;

    // A CRD the batch wrote is looked up now that the write has landed (ML §6).
    if (landed) lookUpCrdsRef.current?.(batch);

    // Anything typed while that request was in flight is queued now. Only
    // chained after a batch that landed — a re-queued failure would otherwise
    // spin here instead of waiting for the next edit or the next manual save.
    if (landed && pending.current.size > 0) return flush();
    // A batch that did not land answers null: the step gate stays on the step rather than judge
    // the state from before the answers (gate 2's finding F, the owner's ruling).
    return landed ? stateRef.current : null;
  }, [adopt, clearTimers, responseId, withdraw]);

  /**
   * Stages one edit. Queues the write and shows the value at once, but does not
   * send it: a choice goes out on a short timer, typed text waits for `commit`.
   */
  const setValue = React.useCallback(
    (address: IAnswerAddress, value: UiValue, usage: ISnapshotUsage) => {
      const key = addressKey(address.usage, address.instanceId, address.instanceKey);
      // A row write carries the row's writable cells, all of them and only those (B4-2): survey-api
      // refuses a computed, N/A or heading cell with the whole batch, and replaces the row whole.
      const columns = rowWriteColumns(usage, address.instanceKey);
      const encoded = encodeAnswer(value, usage.fieldType, columns);
      const isChoice = isChoiceField(usage.fieldType);

      setDrafts((previous) => new Map(previous).set(key, value));

      // The respondent is answering the question a rejection emptied, so the note
      // explaining the empty field has done its job.
      setClearedPrefill((previous) => {
        if (!previous.has(key)) return previous;
        const remaining = new Set(previous);
        remaining.delete(key);
        return remaining;
      });

      // Text no payload of this field's type can carry. It is **held**, never sent and
      // never used to clear: `encodeAnswer` answers null both for an empty control and
      // for `abc` in a count, and spending that one answer on both is what used to erase
      // the typing on blur — or, where an answer was already stored, send a write that
      // deleted it. Anything queued from an earlier, parseable keystroke goes with it:
      // the respondent has moved the field away from that value, so sending it would
      // save something that is no longer on screen.
      if (isMalformed(value, usage.fieldType, columns)) {
        pending.current.delete(key);
        pendingTouch.current.delete(key);
        markMalformed(key, true);
        if (!isChoice) {
          focused.current = key;
          focusedAddress.current = address;
        }
        return;
      }
      markMalformed(key, false);
      // `focused` exists only so `adopt`/`pruneDrafts` never overwrite the caret with the
      // server's normalized value (see the comment on `pruneDrafts` above). A choice has no
      // caret and no control here ever calls `commit` to release it — `dropdown`, `radio` and
      // `multi_select` all save from `onChange`, with no blur to bind it to — so holding
      // `focused` for a choice would only strand its draft entry forever once the write lands,
      // leaving `hasPendingWrites` stuck true. Only a typed field, which `commit` does release,
      // needs to be held.
      if (!isChoice) {
        focused.current = key;
        focusedAddress.current = address;
        setTyping(key);
      }

      // An edit that lands back on the server's own value is not a write. It
      // happens constantly — retyping a digit, undoing a typo — and sending it
      // would move the revision and re-render the form for no change at all.
      // Judged over the same cells: the stored row also holds the engine's computed ones.
      if (sameAnswer(encoded, writableCells(answersRef.current.get(key)?.value ?? null, columns))) {
        pending.current.delete(key);
        // Nothing to wait for: the server's view of this field already matches
        // what is on screen, so its findings can be trusted now.
        if (isChoice) markTouched(key);
        return;
      }

      pending.current.set(key, {
        usage: address.usage,
        instanceId: address.instanceId,
        instanceKey: address.instanceKey,
        value: encoded
      });

      if (isChoice) {
        // A choice is finished the moment it is made, so it is answerable-for as
        // soon as the server has it. Typed text is staged in `commit` instead:
        // while it is being typed there is nothing to report, and the alternative
        // is "an answer is required" under the first character being entered.
        pendingTouch.current.add(key);
        if (choiceTimer.current) window.clearTimeout(choiceTimer.current);
        choiceTimer.current = window.setTimeout(() => void flush(), CHOICE_SAVE_MS);
        return;
      }

      // Re-armed on every keystroke, so it only fires once typing has actually
      // stopped — and `adopt` spares the focused field even then.
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(() => void flush(), IDLE_SAVE_MS);
    },
    [flush, markMalformed, markTouched]
  );

  const commit = React.useCallback(async () => {
    const key = focused.current;
    const address = focusedAddress.current;
    focused.current = null;
    focusedAddress.current = null;
    setTyping(null);
    // Written already while the caret was in it (a background flush): no write of this commit
    // carries it, so its CRD lookup is asked here, now the respondent has left it (finding E).
    const written = key !== null && !pending.current.has(key) && !malformedRef.current.has(key);

    // Leaving a field is the point at which its answer can be judged: whatever is
    // in it now is what the respondent meant. `key` is null when they passed
    // through without typing, and an untouched field stays quiet until submit.
    if (key !== null && pending.current.has(key)) {
      // Still on the wire — staged, and promoted by `adopt` when it lands.
      pendingTouch.current.add(key);
    } else if (key !== null && malformedRef.current.has(key)) {
      // Nothing is queued because nothing could be encoded, and this overlay entry is the
      // only copy of what was typed — so it stays. Judging it now is right for the same
      // reason it is right below: the respondent has left the field, so what is in it is
      // what they meant. This is the branch that used to delete the entry and take the
      // typing with it.
      markTouched(key);
    } else if (key !== null) {
      // Nothing queued: the value never changed, or its write has already landed.
      // Either way the state on screen is the state the server holds, so the
      // field can be judged against it right now.
      markTouched(key);

      // And release the overlay entry that was held back for the caret. Every
      // other entry is either still queued or is a value the server refused —
      // which the form must keep showing next to the refusal that names it.
      setDrafts((previous) => {
        if (!previous.has(key)) return previous;
        const remaining = new Map(previous);
        remaining.delete(key);
        return remaining;
      });
    }

    await flush();
    if (written && address) lookUpCrdsRef.current?.([address]);
  }, [flush, markTouched]);

  // Leaving the tab is the last reliable moment to save: `pagehide` fires too
  // late for a request that needs an Authorization header to finish.
  React.useEffect(() => {
    const saveIfHidden = () => {
      if (document.visibilityState === 'hidden' && pending.current.size > 0) void flush();
    };

    document.addEventListener('visibilitychange', saveIfHidden);
    return () => document.removeEventListener('visibilitychange', saveIfHidden);
  }, [flush]);

  /**
   * Structural actions and submit all go through the same shape: land the
   * pending answers first (so the server evaluates against what the respondent
   * actually typed), then run the action, then adopt the state it returns.
   */
  const runAction = React.useCallback(
    async (
      action: (currentRevision: number) => Promise<IResponseState>,
      fallback: string,
      /** A refusal the caller says itself, beside its control, rather than in the banner. */
      ownRefusal?: (error: unknown) => boolean,
      /**
       * A call nobody pressed — a lookup: its landing leaves the banner alone, which says what
       * one of the respondent's own presses or saves met (gate 2's finding D).
       */
      background = false
    ) => {
      if (!responseId) return;
      await flush();

      try {
        // Taken out of play, by the flush just now or before: the action is not sent, and its
        // caller is answered as refused — `submit` must not read a press that never went as done.
        if (withdrawnRef.current) throw new ResponseGone();
        let next: IResponseState;
        try {
          next = await action(revision.current);
        } catch (actionError) {
          if (
            actionError instanceof ApiError &&
            actionError.isProblem(ProblemType.revisionConflict)
          ) {
            const fresh = await surveyApi.getResponse(responseId);
            if (!fresh) throw new ResponseGone();
            revision.current = fresh.revision;
            next = await action(revision.current);
          } else {
            throw actionError;
          }
        }
        adopt(next);
        if (!background) setError(null);
      } catch (actionError) {
        // Same gates as the ones in `flush`, on the four action routes they also cover —
        // repeats, acks, prefill confirmations and submit — and the lookups. The error still
        // reaches the caller: `submit` reads `submit-blocked` off it, and nothing about a closed
        // cycle or a response taken out of play changes who handles what.
        if (actionError instanceof ApiError && actionError.isProblem(ProblemType.cycleClosed)) {
          setCycleClosed(true);
          setError(CYCLE_CLOSED_MESSAGE);
        } else if (isGone(actionError)) {
          withdraw();
        } else if (!ownRefusal?.(actionError)) {
          setError(errorMessage(actionError, fallback));
        }
        throw actionError;
      }
    },
    [adopt, flush, responseId, withdraw]
  );

  const addRow = React.useCallback(
    async (usageCode: string) => {
      await runAction(
        (current) => surveyApi.addRepeatRow(responseId!, current, usageCode),
        'Could not add a row'
      ).catch(() => undefined);
    },
    [responseId, runAction]
  );

  const removeRow = React.useCallback(
    async (instanceId: string) => {
      await runAction(
        (current) => surveyApi.removeRepeatRow(responseId!, current, instanceId),
        'Could not remove that row'
      ).catch(() => undefined);
    },
    [responseId, runAction]
  );

  const acknowledge = React.useCallback(
    async (finding: IFinding) => {
      // A synthesized finding has no ruleId and is unackable by construction.
      if (!finding.ruleId) return;
      await runAction(
        (current) =>
          surveyApi.acknowledge(responseId!, current, [
            {
              ruleId: finding.ruleId!,
              usage: finding.usage,
              instanceId: finding.instanceId,
              instanceKey: finding.instanceKey
            }
          ]),
        'Could not acknowledge that warning'
      ).catch(() => undefined);
    },
    [responseId, runAction]
  );

  /**
   * One CRD lookup, run from the queue (B6-2, ML §6): through `runAction` — the flush first, a
   * `revision-conflict` re-read and one retry, `cycle-closed` as every action — on a draft alone
   * (survey-api answers any other `response-not-writable`), the answered state adopted. A 422 is
   * the row's to say (`ILookupNote.reason`), never the banner; `unknown_row` — a row removed under
   * the queue — re-reads quietly.
   */
  const runLookup = React.useCallback(
    async (rowKey: string) => {
      queuedRows.current.delete(rowKey);
      const current = stateRef.current;
      if (
        !responseId ||
        current?.status !== 'draft' ||
        cycleClosedRef.current ||
        withdrawnRef.current
      ) {
        return;
      }

      const note = (next: Nullable<ILookupNote>) =>
        setLookups((previous) => {
          const copy = new Map(previous);
          if (next) copy.set(rowKey, next);
          else copy.delete(rowKey);
          return copy;
        });
      note(namelessNote('pending', ''));

      let answer: Nullable<IMemberLookupAnswer | IRespondentLookupAnswer> = null;
      try {
        await runAction(
          async (baseRevision) => {
            const document =
              rowKey === RESPONDENT_LOOKUP
                ? await surveyApi.lookupRespondent(responseId, baseRevision)
                : await surveyApi.lookupMember(responseId, rowKey, baseRevision);
            if (!document) throw new LookupNotSent();
            answer = document.lookup;
            return document.state;
          },
          'Could not look this CRD up',
          (lookupError) =>
            lookupError instanceof LookupNotSent ||
            invalidActionProblems(lookupError).length > 0 ||
            unanswered(lookupError),
          true
        );
      } catch (lookupError) {
        // Taken out of play under the queue: the form's notice says it, not the row.
        if (isGone(lookupError)) return note(null);
        const problems = invalidActionProblems(lookupError);
        if (problems.some((problem) => problem.reason === 'unknown_row')) {
          note(null);
          // A row removed under the queue: re-read quietly. A failed re-read changes nothing on
          // screen — the next write or press reads again.
          const fresh = await surveyApi.getResponse(responseId).catch(() => null);
          if (fresh) adopt(fresh);
          return;
        }
        // Said beside the CRD as no answer, like a network failure — but a fault of this code (a
        // 200 of another shape, a throw in `adopt`) is not a registry's, so it is logged too.
        if (!(lookupError instanceof ApiError) && !(lookupError instanceof LookupNotSent)) {
          console.error('A CRD lookup failed in the client', lookupError);
        }
        const crd = crdOfRow(rowKey);
        lookedUp.current.set(rowKey, crd);
        if (unanswered(lookupError) && !(lookupError instanceof LookupNotSent)) {
          note(namelessNote('failed', crd));
          return;
        }
        note(
          problems.length > 0
            ? { ...namelessNote('refused', crd), reason: problems[0].reason }
            : null
        );
        return;
      }

      const crd = crdOfRow(rowKey);
      lookedUp.current.set(rowKey, crd);
      const looked = answer as Nullable<IMemberLookupAnswer | IRespondentLookupAnswer>;
      if (!looked) return note(null);
      note({
        status: looked.status,
        crd,
        firstName: looked.firstName,
        lastName: looked.lastName,
        firmName:
          'firmName' in looked ? looked.firmName : (looked.brokerDealer ?? looked.riaFirmName)
      });
    },
    // `crdOfRow` reads refs alone, so it is no dependency.
    [adopt, responseId, runAction]
  );

  /** The CRD a row holds in the state last adopted, as text — `''` for none. */
  const crdOfRow = (rowKey: string): string => {
    const usages = new Set(definitionRef.current?.usages.map((usage) => usage.code) ?? []);
    const respondent = respondentShapeOf(usages);
    const crdUsage =
      rowKey === RESPONDENT_LOOKUP
        ? respondent?.crd
        : memberShapesOf(usages).find((shape) =>
            stateRef.current?.repeats.some(
              (row) => row.instanceId === rowKey && row.usage === shape.group
            )
          )?.crd;
    if (!crdUsage) return '';
    return crdText(
      answersRef.current.get(addressKey(crdUsage, rowKey === RESPONDENT_LOOKUP ? null : rowKey, ''))
    );
  };

  /** Queues a row's lookup behind the ones already out — one at a time, in order. */
  const requestLookup = React.useCallback(
    (rowKey: string) => {
      if (queuedRows.current.has(rowKey)) return;
      queuedRows.current.add(rowKey);
      lookupQueue.current = lookupQueue.current
        .then(() => runLookup(rowKey))
        .catch((queueError: unknown) => console.error('A CRD lookup failed in the client', queueError));
    },
    [runLookup]
  );

  // A member's or the respondent's CRD stored with a value is looked up, once per value (moment
  // (a)): after a write carrying it lands, or — written while the caret was still in it — once the
  // respondent leaves it (`commit`). Never while the caret is in it: a background flush (the idle
  // backstop, a lookup's own) sends half a number, and survey-api would look that one up and pin
  // whoever holds it on the row (gate 2's finding E).
  lookUpCrdsRef.current = (written) => {
    const definitionNow = definitionRef.current;
    if (!definitionNow) return;
    const usages = new Set(definitionNow.usages.map((usage) => usage.code));
    const shapes = memberShapesOf(usages);
    const memberCrds = new Set(shapes.map((shape) => shape.crd));
    const respondentCrd = respondentShapeOf(usages)?.crd;
    for (const address of written) {
      if (addressKey(address.usage, address.instanceId, address.instanceKey) === focused.current) {
        continue;
      }
      // A registered radio answered Yes again over a kept CRD asks for it again, as the CRD would.
      const registered = shapes.find((shape) => shape.registered === address.usage);
      let rowKey: Nullable<string> = null;
      if (memberCrds.has(address.usage) && address.instanceId) {
        rowKey = address.instanceId;
      } else if (registered && address.instanceId) {
        const answer = answersRef.current.get(addressKey(address.usage, address.instanceId, ''));
        if (selectedCodes(answer).includes(registered.registeredYes)) rowKey = address.instanceId;
      } else if (address.usage === respondentCrd && address.instanceId === null) {
        rowKey = RESPONDENT_LOOKUP;
      }
      if (!rowKey) continue;
      const crd = crdOfRow(rowKey);
      if (crd === '' || lookedUp.current.get(rowKey) === crd) continue;
      requestLookup(rowKey);
    }
  };

  // Whenever a state lands: Teams' row 1 with a blank CRD (moment (b)) and a row whose CRD is a
  // proposal (moment (c)) are looked up once a session, on a draft. A row looked up at a CRD whose
  // CRD went blank or whose radio left Yes is forgotten, with its note: survey-api dropped what its
  // lookup left, so the same CRD given again is looked up again (the survey review's G11, G12).
  afterAdoptRef.current = (next) => {
    const definitionNow = definitionRef.current;
    if (
      !definitionNow ||
      next.status !== 'draft' ||
      cycleClosedRef.current ||
      withdrawnRef.current
    ) {
      return;
    }
    const usages = new Set(definitionNow.usages.map((usage) => usage.code));
    for (const shape of memberShapesOf(usages)) {
      for (const row of next.repeats) {
        if (row.usage !== shape.group || row.status !== 'active') continue;
        const crd = answersRef.current.get(addressKey(shape.crd, row.instanceId, ''));
        const registered = answersRef.current.get(addressKey(shape.registered, row.instanceId, ''));
        // A lookup of a blank CRD (Teams' row 1) left nothing to drop, and its note stays.
        if (
          lookedUp.current.get(row.instanceId) &&
          (crdText(crd) === '' || !selectedCodes(registered).includes(shape.registeredYes))
        ) {
          lookedUp.current.delete(row.instanceId);
          setLookups((previous) => {
            if (!previous.has(row.instanceId)) return previous;
            const copy = new Map(previous);
            copy.delete(row.instanceId);
            return copy;
          });
        }
        if (lookedUp.current.has(row.instanceId) || askedRows.current.has(row.instanceId)) continue;
        const proposedCrd = crd?.provenance?.state === 'proposed' && crdText(crd) !== '';
        const blankLeadRow = shape.leadRow && row.ordinal === 1 && crdText(crd) === '';
        if (!proposedCrd && !blankLeadRow) continue;
        askedRows.current.add(row.instanceId);
        requestLookup(row.instanceId);
      }
    }
  };

  /**
   * The subset of `candidates` survey-api would still accept a decision about, read off the
   * state this hook last adopted.
   *
   * The batch is validated **all-or-nothing** (`ResponseActionService.ConfirmPrefillAsync`),
   * so one stale address does not fail alone — it discards every valid item beside it. A
   * step's worth of proposals is exactly where that costs something, which is why the batch
   * is rebuilt here rather than sent as the render handed it over.
   *
   * A malformed address is dropped too: nothing was sent for it, so the server still calls
   * it `proposed` while the screen holds text that could not be encoded. Confirming there
   * would settle a value the respondent has already moved away from.
   */
  const stillProposed = React.useCallback((candidates: IAnswerAddress[]): IAnswerAddress[] => {
    return candidates.filter((address) => {
      const key = addressKey(address.usage, address.instanceId, address.instanceKey);
      if (malformedRef.current.has(key)) return false;
      return answersRef.current.get(key)?.provenance?.state === 'proposed';
    });
  }, []);

  const confirmPrefill = React.useCallback(
    async (addresses: IAnswerAddress[], action: 'confirm' | 'reject') => {
      if (addresses.length === 0) return;

      // Land what is typed before deciding what is still a proposal. `runAction` would
      // flush anyway; doing it here means the rebuild below sees the result — a field the
      // respondent answered has become `overridden` by now and must leave the batch,
      // rather than take the rest of the step down with it.
      await flush();

      const send = async (batch: IAnswerAddress[]): Promise<boolean> => {
        try {
          await runAction(
            (current) =>
              surveyApi.confirmPrefill(
                responseId!,
                current,
                batch.map((address) => ({
                  usage: address.usage,
                  instanceId: address.instanceId,
                  instanceKey: address.instanceKey,
                  action
                }))
              ),
            'Could not record that decision'
          );
          return true;
        } catch (confirmError) {
          // A decision already recorded is not a failure to report: another tab, a double
          // click, or a write that landed between the rebuild and this request. Nothing is
          // left for the respondent to do about those, so the banner comes down and the
          // caller decides whether anything is worth retrying.
          if (!isSettledPrefill(confirmError)) {
            // A lock's reject is refused `locked_usage` or `lead_locked` — offered by no control
            // here, so said in words naming the question, over `runAction`'s title.
            const locked = lockedTargets(confirmError);
            if (locked.length > 0) {
              const named = locked.map(({ target: code }) => {
                const label = definition?.usages.find((usage) => usage.code === code)?.label;
                return label ? `${label} (${code})` : code;
              });
              setError(`Not recorded: ${named.join(', ')} — ${refusalWords(locked[0].reason)}.`);
            }
            return false;
          }
          setError(null);
          const fresh = await surveyApi.getResponse(responseId!);
          // Quietly, not through `load()`: that sets `status: 'loading'` and replaces the
          // whole form with its skeleton, which is a lot of screen movement to report that
          // a click changed nothing.
          if (fresh) adopt(fresh);
          return false;
        }
      };

      // Two attempts at most — the design's §5 — each addressed at what the state in hand
      // still calls `proposed`, so the retry is built from what the re-read adopted rather
      // than from what was refused. Only two: a second refusal means the addresses are
      // moving faster than a decision can be recorded, and re-sending forever would be a
      // loop rather than a recovery.
      let batch: IAnswerAddress[] = [];
      let recorded = false;
      for (let attempt = 0; attempt < 2 && !recorded; attempt += 1) {
        batch = stillProposed(addresses);
        if (batch.length === 0) return;
        recorded = await send(batch);
      }
      if (!recorded) return;

      if (action !== 'reject') return;
      const keys = batch.map((address) =>
        addressKey(address.usage, address.instanceId, address.instanceKey)
      );

      // A rejection empties the field, and nothing else would mark it answerable-for:
      // `touched` is filled on blur and on a choice, and a rejected field was never typed
      // into. Without this the `engine.required` that replaces the answer stays suppressed
      // by `visibleFindings` and the field simply goes blank.
      setTouched((previous) => withKeys(previous, keys));
      setClearedPrefill((previous) => withKeys(previous, keys));
    },
    [adopt, definition, flush, responseId, runAction, stillProposed]
  );

  const unlinkMember = React.useCallback(
    async (instanceId: string, names: IAnswerAddress[]) => {
      try {
        await runAction(
          async (current) => {
            const next = await surveyApi.unlinkMember(responseId!, instanceId, current);
            if (!next) throw new Error('This row cannot be undone here');
            return next;
          },
          'Could not undo that lookup',
          // The row removed in another tab, or become the lead's since the page was drawn: what
          // is stale is the page, so it is read again quietly, as a lookup's `unknown_row` is.
          (unlinkError) => {
            const stale = invalidActionProblems(unlinkError).some((problem) =>
              STALE_UNLINK_REASONS.has(problem.reason)
            );
            if (stale) {
              void surveyApi
                .getResponse(responseId!)
                .then((fresh) => fresh && adopt(fresh))
                .catch(() => undefined);
            }
            return stale;
          }
        );
      } catch {
        return;
      }
      // The CRD stays, and so does `lookedUp`'s value for it: the same CRD would find the same
      // person. The note naming them goes.
      setLookups((previous) => {
        const copy = new Map(previous);
        copy.delete(instanceId);
        return copy;
      });
      // Only the names survey-api dropped: one the respondent had typed over (`overridden`) is
      // theirs and stays, and saying it was cleared would be untrue.
      const keys = names
        .map((name) => addressKey(name.usage, name.instanceId, name.instanceKey))
        .filter((key) => !answersRef.current.has(key));
      setTouched((previous) => withKeys(previous, keys));
      setClearedPrefill((previous) => withKeys(previous, keys));
    },
    [adopt, responseId, runAction]
  );

  /**
   * Fire-and-forget: the cursor is outside the revision protocol on purpose —
   * a UI position where last-write-wins between the respondent's own tabs is
   * the wanted semantics — so a failure here must never interrupt filling.
   */
  const saveCursor = React.useCallback(
    (cursor: ICursor) => {
      // Taken out of play, the cursor's route answers 404 like every other.
      if (!responseId || withdrawnRef.current) return;
      void surveyApi.setCursor(responseId, cursor).catch((cursorError) => {
        console.warn('[Survey] Could not save the step position:', cursorError);
      });
    },
    [responseId]
  );

  const submit = React.useCallback(async (): Promise<boolean> => {
    if (!responseId) return false;
    setIsSubmitting(true);
    setSubmitReport(null);
    // From the first submit attempt on, every finding is shown — the respondent
    // has said they are done, so withholding "this is required" would be hiding
    // exactly what stands between them and a successful submit.
    setRevealFindings(true);

    try {
      await runAction(
        (current) => surveyApi.submit(responseId, current),
        'Could not submit the survey'
      );
      return true;
    } catch (submitError) {
      if (submitError instanceof ApiError && submitError.isProblem(ProblemType.submitBlocked)) {
        setSubmitReport((submitError.problem?.report as SubmitReport) ?? []);
        setError('Some answers still need attention before this can be submitted.');
        // The gate ran a full evaluation; re-reading picks up the findings it
        // produced, so the form marks the same fields the report names.
        await load();
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [load, responseId, runAction]);

  const view = React.useMemo(
    () => (definition ? buildDefinitionView(definition) : null),
    [definition]
  );

  const pendingPrefill = React.useMemo(
    () => (state && view ? pendingConfirmations(state, view) : []),
    [state, view]
  );

  /** Each draft as it was when its field was last left — what a table row being typed in again,
   *  or a number gone back to, stands for in the prediction meanwhile. */
  const leftAs = React.useRef(new Map<string, Nullable<AnswerPayload>>());
  // The answers the rules read beyond survey-api's: what is queued or on the wire — a choice at
  // once, a typed field as it was last left. Never a held entry, nor a draft a refusal or a closed
  // cycle left unsent: survey-api will not see them. The three states the deps name besides the
  // drafts are the moments the queue drops a batch.
  const render = React.useMemo(() => {
    if (!definition || !state || !view) return null;
    const overlay = new Map<string, Nullable<AnswerPayload>>();
    for (const key of [...leftAs.current.keys()]) {
      if (!drafts.has(key)) leftAs.current.delete(key);
    }
    for (const [key, value] of drafts) {
      if (malformed.has(key)) continue;
      if (!pending.current.has(key) && !inflightKeys.current.has(key)) continue;
      if (key === typing) {
        if (leftAs.current.has(key)) overlay.set(key, leftAs.current.get(key)!);
        continue;
      }
      const address = addressOf(key);
      const usage = view.usagesByCode.get(address.usage);
      if (!usage) continue;
      const columns = rowWriteColumns(usage, address.instanceKey);
      const encoded = encodeAnswer(value, usage.fieldType, columns);
      leftAs.current.set(key, encoded);
      overlay.set(key, encoded);
    }
    return predictRender(definition, state, overlay);
  }, [definition, state, view, drafts, typing, malformed, rejections, cycleClosed, withdrawn]);

  // Each entry survey-api's render keeps from the prediction, logged the first time it is met: a
  // usage off its path (the runtime design's §9), or a sign this engine has drifted from survey-api.
  const disagreementsLogged = React.useRef(new Set<string>());
  React.useEffect(() => {
    if (!definition || !state) return;
    const fresh = ruleDisagreements(definition, state).filter(
      (line) => !disagreementsLogged.current.has(line)
    );
    if (fresh.length === 0) return;
    fresh.forEach((line) => disagreementsLogged.current.add(line));
    console.warn('The form’s rules disagree with survey-api’s render here', fresh);
  }, [definition, state]);

  return {
    status,
    error,
    definition,
    view,
    state,
    render,
    drafts,
    isSaving,
    isSubmitting,
    // Read off the overlay rather than the queue ref: the overlay is state, so
    // this stays honest across renders.
    hasPendingWrites: drafts.size > 0,
    lastSavedAt,
    rejections,
    submitReport,
    touched,
    malformed,
    revealFindings,
    revealedSteps,
    revealStep,
    cycleClosed,
    withdrawn,
    setValue,
    commit,
    flush,
    addRow,
    removeRow,
    acknowledge,
    confirmPrefill,
    unlinkMember,
    pendingPrefill,
    clearedPrefill,
    lookups,
    saveCursor,
    submit,
    reload: () => load()
  };
}

/** A key set with `keys` added, holding the previous set's identity when none of them is
 *  new — which is what keeps a decision that changes nothing from re-rendering the form. */
function withKeys(previous: Set<string>, keys: readonly string[]): Set<string> {
  const next = new Set(previous);
  for (const key of keys) next.add(key);
  return next.size === previous.size ? previous : next;
}

/** `previous` without `keys`, itself when none was there. */
function withoutKeys<T extends Set<string> | Map<string, unknown>>(previous: T, keys: string[]): T {
  if (keys.length === 0) return previous;
  const next = (previous instanceof Map ? new Map(previous) : new Set(previous)) as T;
  for (const key of keys) next.delete(key);
  return next;
}

/** A lookup addressed at an id that is no GUID — answered `null` unsent by the API module. */
class LookupNotSent extends Error {}

/** The response is gone for this respondent, found so without a 404 of its own: a conflict's
 *  re-read answered null, or the form was already `withdrawn` when an action was asked. */
class ResponseGone extends Error {}

/**
 * The response is no longer this respondent's to write (`withdrawn`): survey-api's 404 on a write
 * or an action, which it answers for a response its caller cannot reach — its recycle bin's, since
 * SR-470 — before it judges the cycle, the status or the revision.
 */
function isGone(error: unknown): boolean {
  return error instanceof ResponseGone || (error instanceof ApiError && error.status === 404);
}

/** An answer that never came — no `ApiError`, or a 5xx — rather than a refusal. */
function unanswered(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

/** A 422's problems (`invalid-action`) — a lookup's or a confirmation's — `[]` for anything else. */
function invalidActionProblems(error: unknown): { target: string; reason: string }[] {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.invalidAction)) return [];
  return error.problem?.problems ?? [];
}

/** A lookup's note before any name is known: pending, failed or refused. */
function namelessNote(status: ILookupNote['status'], crd: string): ILookupNote {
  return { status, crd, firstName: null, lastName: null, firmName: null };
}

/** A CRD answer's value as text — an `int`, or text a July definition may carry — `''` for none. */
function crdText(answer: IAnswer | undefined): string {
  const value = answer?.value;
  if (!value || value.t === 'cells' || value.t === 'opts') return '';
  return String(value.v).trim();
}

/** The refusals of a confirmation batch over a lock, prefill's or the lead's — see
 *  `REFUSAL_WORDS`. */
function lockedTargets(error: unknown): { target: string; reason: string }[] {
  return invalidActionProblems(error).filter(
    (problem) => problem.reason === 'locked_usage' || problem.reason === 'lead_locked'
  );
}

/** See `SETTLED_PREFILL_REASONS`. Every entry must be settled: a batch refused for one
 *  address the respondent answered and one this app addressed wrongly is still a bug. */
function isSettledPrefill(error: unknown): boolean {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.invalidAction)) return false;
  const problems = error.problem?.problems ?? [];
  return (
    problems.length > 0 && problems.every((problem) => SETTLED_PREFILL_REASONS.has(problem.reason))
  );
}
